import { supabase } from './supabase'
import { fetchAll } from './fetchall'
import { hasCadence } from './scheduled-generation'
import { localDate } from './campaigns'
import { dueTouches, loadDrips } from './drips'
import { loadHeldBulkSends, loadQueue } from './outbox'

/**
 * The nav-tab counters, one file because they're one recurring request, not
 * several, even though each count comes from a different table. Started
 * 2026-08-22 with Tasks, Bounces, and Scheduled Sends; Drips and Outbox
 * followed the next day once Chris noticed those two were the only tabs on
 * the "check this daily" path still without one.
 *
 * The Tasks/Bounces/Scheduled counts below are head-counts (`count: 'exact',
 * head: true` where the filter allows it) — cheap, because this is a number
 * shown in the nav on every screen, not a list. Drips and Outbox can't be:
 * both need a real answer to "is this actually due/waiting", which lives in
 * pure logic (`dueTouches`, the "still waiting" test in `outbox.ts`) rather
 * than something PostgREST's own filter syntax can express — so both reuse
 * the exact same functions the tabs themselves are built on, rather than a
 * second, cheaper-looking copy of the question that could quietly disagree
 * with what the tab actually shows.
 */

/**
 * Open tasks due today or already overdue — not every open task, which
 * would count things she has weeks to get to and blunt the number as a
 * signal. Matches `isOverdue`/`isDueToday` in `tasksort.ts` (not done, and
 * `due_date <= today`) without needing to fetch every row just to run those
 * pure checks — `due_date` sorts and compares lexically the same way those
 * two functions already rely on.
 */
export async function countDueTasks(
  today = localDate(new Date()),
): Promise<{ count: number | null; error: string | null }> {
  const { count, error } = await supabase
    .from('tasks')
    .select('id', { count: 'exact', head: true })
    .eq('done', false)
    .lte('due_date', today)
  return { count: count ?? null, error: error?.message ?? null }
}

/**
 * Every bounce currently on record. There is no "addressed" flag —
 * `deleteBounce` (see `bounces.ts`) IS how one gets marked handled, by
 * removing it — so a plain count of what is left is exactly "how many still
 * need attention", not an approximation of it.
 */
export async function countBounces(): Promise<{ count: number | null; error: string | null }> {
  const { count, error } = await supabase.from('bounces').select('id', { count: 'exact', head: true })
  return { count: count ?? null, error: error?.message ?? null }
}

/**
 * Recurring campaigns currently active — enabled, and actually a recurring
 * template rather than an ordinary one-off blast or one of its own generated
 * children (both of which carry `cadence: {}` — see `hasCadence`'s own
 * comment in `scheduled-generation.ts` for why that column alone tells them
 * apart). Not a `head: true` count: `hasCadence` is a real predicate over the
 * jsonb value, not a comparison PostgREST's own filter syntax can express
 * safely, so this reads the three narrow columns it needs and answers the
 * same question `Scheduled.tsx`'s own `isRecurring` does — reusing it rather
 * than a second copy that could quietly disagree. The campaigns table is
 * small enough (one-off blasts included) that a single-page `fetchAll` of
 * three columns costs little next to a screen-wide nav bar re-rendering.
 */
export async function countActiveRecurringCampaigns(): Promise<{
  count: number | null
  error: string | null
}> {
  const { rows, error } = await fetchAll<{ id: string; cadence: unknown; is_enabled: boolean }>(
    'campaigns',
    'id,cadence,is_enabled',
    ['id'],
  )
  if (error) return { count: null, error }
  return { count: rows.filter((c) => c.is_enabled && hasCadence(c.cadence)).length, error: null }
}

/**
 * Agents with a touch due right now, across every active drip campaign —
 * not a count of campaigns, and not "how many touches exist": the same
 * number `Drips.tsx` itself would show if you added up every "Touch N is
 * due for M agents" line on the page. Built from `loadDrips()` and
 * `dueTouches()`, the exact functions the tab calls per campaign, rather
 * than a lighter-weight re-derivation of "due" that could disagree with
 * what pressing Approve actually acts on.
 */
export async function countDueDripTouches(
  today = localDate(new Date()),
): Promise<{ count: number | null; error: string | null }> {
  const bundle = await loadDrips()
  if (bundle.error) return { count: null, error: bundle.error }
  let count = 0
  for (const c of bundle.campaigns) {
    const touches = bundle.touches.filter((t) => t.campaign_id === c.id)
    const enrolments = bundle.enrolments.filter((e) => e.campaign_id === c.id)
    const sentFor = new Set<string>()
    for (const key of bundle.sent) {
      const [cid, agentId, step] = key.split(' ')
      if (cid === c.id) sentFor.add(`${agentId} ${step}`)
    }
    for (const due of dueTouches(c, touches, enrolments, sentFor, today)) {
      count += due.agents.length
    }
  }
  return { count, error: null }
}

/**
 * Everything the Outbox tab itself would call "waiting" — reviewed/milestone
 * drafts (`loadQueue`, the same read the tab's own list uses) plus bulk sends
 * held and not yet sent, whether generated by a recurring campaign or
 * composed by hand (`loadHeldBulkSends`, shared with `Outbox.tsx`'s own
 * `HeldBulkSends` section, added 2026-08-23 specifically so this count and
 * that section can't drift apart — and broadened the same day to cover a
 * self-composed bulk send too, which never surfaced here before). Not a
 * head-count: `loadQueue` already pages the whole table to warn about a
 * silent 1000-row cap, and re-deriving just a count from PostgREST directly
 * would drop that safety net for the one place nothing else reads the
 * result to notice.
 */
export async function countOutboxWaiting(): Promise<{ count: number | null; error: string | null }> {
  const [q, s] = await Promise.all([loadQueue(), loadHeldBulkSends()])
  if (q.error) return { count: null, error: q.error }
  if (s.error) return { count: null, error: s.error }
  return { count: (q.rows?.length ?? 0) + (s.rows?.length ?? 0), error: null }
}
